// ---------------------------------------------------------------------------
// Shared R2 Group Chat API types
// Multi-user chat scoped to a single document (see docs: requests_document_group_chat.md).
// Used by both backend (NestJS) and frontend clients.
// ---------------------------------------------------------------------------

export enum GroupChatApiResult
{
    Success = 0,
    FailTokenInvalid = -1,
    FailTokenExpired = -2,
    FailAccessDenied = -3,
    FailServiceTemporaryNotValid = -4,
    FailInvalidParameters = -5,
}

/** Document vendor. Case sensitive on the wire. */
export type GroupChatVendorId = 'google' | 'microsoft';

/** Document type. Case sensitive constant on the wire. */
export type GroupChatDocumentType =
    | 'word'
    | 'excel'
    | 'mail'
    | 'power_point'
    | 'note';

/** Identifies the document a group chat is attached to. */
export interface IGroupChatDocument
{
    vendor_id: GroupChatVendorId;
    type: GroupChatDocumentType;
    id: string;
}

/** A single group-chat message as returned by the API. */
export interface IGroupChatMsg
{
    user_email: string;
    id: number;
    /** Parent message identifier; default -1 (no parent). */
    parent_id: number;
    text: string;
    /** Arbitrary JSON payload attached to the message; may be null. */
    meta?: Record<string, unknown> | null;
    /** Unix timestamp (seconds). */
    dt: number;
}

/** A group-chat participant. */
export interface IGroupChatUser
{
    user_email: string;
    first_name: string;
    last_name: string;
    display_name: string;
    /** Total messages by this user in the document (users/get only). */
    msgs_count?: number;
}

// --- group-chat/user/msg/add ---

export interface IGroupChatMsgAddRequest
{
    document: IGroupChatDocument;
    text: string;
    parent_id?: number;
    meta?: Record<string, unknown>;
}

export interface IGroupChatMsgAddResponse
{
    result: GroupChatApiResult;
    msg?: IGroupChatMsg;
}

// --- group-chat/user/msgs/get ---

export interface IGroupChatMsgsGetRequest
{
    document: IGroupChatDocument;
    msg_id_from?: number;
    /** Items per page (min 10, max 100). Default 10. */
    page_size?: number;
    /** Ordering by creation dt. Default 'asc'. */
    msg_order?: 'asc' | 'desc';
}

export interface IGroupChatMsgsGetResponse
{
    result: GroupChatApiResult;
    msgs?: IGroupChatMsg[];
    /** Unique senders associated with the messages on the current page. */
    users?: IGroupChatUser[];
    /** Set as msg_id_from on the next request to paginate. */
    next_msg_id_from?: number;
}

// --- group-chat/user/msg/get ---

export interface IGroupChatMsgGetRequest
{
    document: IGroupChatDocument;
    msg_id: number;
}

export interface IGroupChatMsgGetResponse
{
    result: GroupChatApiResult;
    msg?: IGroupChatMsg;
    user?: IGroupChatUser;
}

// --- group-chat/users/get ---

export interface IGroupChatUsersGetRequest
{
    document: IGroupChatDocument;
    /** Pagination cursor from the previous response. */
    cursor?: string;
    /** Items per page (min 10, max 100). Default 10. */
    page_size?: number;
}

export interface IGroupChatUsersGetResponse
{
    result: GroupChatApiResult;
    users?: IGroupChatUser[];
    /** Cursor for the next page; empty when no more pages. */
    next_cursor?: string;
}

// --- group-chat/user/msg/delete ---

export interface IGroupChatMsgDeleteRequest
{
    document: IGroupChatDocument;
    msg_ids: number[];
}

export interface IGroupChatMsgDeleteResponse
{
    result: GroupChatApiResult;
}

// --- group-chat/user/msgs/delete/all ---

export interface IGroupChatMsgsDeleteAllRequest
{
    document: IGroupChatDocument;
}

export interface IGroupChatMsgsDeleteAllResponse
{
    result: GroupChatApiResult;
}
